import { getActiveUserProgramOptions } from "@/actions/user-program/queries";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { seo } from "@/utils/seo";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Check, Dumbbell, Mail, Package, Phone } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className || "h-5 w-5"}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.938 3.658 1.434 5.71 1.435h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.417" />
    </svg>
  );
}

const paymentSuccessSearchSchema = z.object({
  type: z.enum(["package", "program", "product"]).catch("package"),
  id: z.string().optional(),
  paymentId: z.string().optional(),
});

export const Route = createFileRoute("/_app/payment/success")({
  validateSearch: (search) => paymentSuccessSearchSchema.parse(search),
  head: () => ({
    meta: seo({
      title: "Ödeme Başarılı - theCoach",
      description: "Satın alma işleminiz başarıyla tamamlandı.",
    }),
  }),
  component: PaymentSuccessPage,
});

function PaymentSuccessPage() {
  const search = Route.useSearch();
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["amICoachingClient"] });
    queryClient.invalidateQueries({
      queryKey: getActiveUserProgramOptions().queryKey,
    });
  }, [queryClient]);

  const isPackage = search.type === "package";
  const isProgram = search.type === "program";
  const isProduct = search.type === "product";

  const whatsappMessage = encodeURIComponent(
    "Merhaba Alican hocam, koçluk paketini satın aldım. Süreci başlatmak için yazıyorum.",
  );
  const whatsappUrl = `https://wa.me/905449642824?text=${whatsappMessage}`;

  const mailSubject = encodeURIComponent("Koçluk Paketi Satın Alımı");
  const mailBody = encodeURIComponent(
    "Merhaba Alican Hocam,\n\nKoçluk paketini satın aldım. Koçluk sürecimi başlatmak ve detayları planlamak için iletişime geçmek istiyorum.",
  );
  const mailUrl = `mailto:atalaythecoach@gmail.com?subject=${mailSubject}&body=${mailBody}`;

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 pt-24 pb-16">
      <div className="w-full max-w-lg">
        <Card className="border border-border bg-card shadow-sm">
          <CardContent className="p-6 text-center sm:p-8">
            {/* Success Icon */}
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Check className="h-8 w-8 stroke-[2.5]" />
            </div>

            {/* Title */}
            <h1 className="font-bold text-foreground text-xl tracking-tight sm:text-2xl">
              Ödemeniz Başarıyla Alındı
            </h1>

            {/* Description */}
            <div className="mt-3 space-y-2 text-muted-foreground text-sm leading-relaxed">
              {isPackage && (
                <>
                  <p>
                    Koçluk paketiniz başarıyla tanımlandı.{" "}
                    <span className="font-medium text-foreground">
                      Koçunuza bilgilendirme iletilmiştir.
                    </span>
                  </p>
                  <p className="text-xs sm:text-sm">
                    Koçluk sürecinizi hemen başlatmak ve ilk değerlendirmeyi
                    yapmak için WhatsApp veya E-posta üzerinden koçunuzla
                    iletişime geçebilirsiniz.
                  </p>
                </>
              )}
              {isProgram && (
                <p>
                  Antrenman programınız hesabınıza tanımlandı. Detayları program
                  sayfanızdan hemen inceleyebilirsiniz.
                </p>
              )}
              {isProduct && (
                <p>Siparişiniz başarıyla alındı ve hazırlanmaya başlandı.</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              {isPackage && (
                <>
                  <Button
                    asChild
                    size="lg"
                    className="h-12 w-full gap-2 rounded-xl bg-[#25D366] font-bold text-white shadow-sm hover:bg-[#20ba5a] active:scale-[0.99]"
                  >
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <WhatsAppIcon className="h-5 w-5 fill-current" />
                      WhatsApp ile İletişime Geç
                    </a>
                  </Button>

                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="h-11 w-full gap-2 rounded-xl border-border font-medium hover:bg-accent"
                  >
                    <a href={mailUrl}>
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      E-posta ile İletişime Geç
                    </a>
                  </Button>

                  {/* Coach Contact Details Box */}
                  <div className="mt-4 rounded-xl border border-border/70 bg-muted/40 p-4 text-left">
                    <p className="font-semibold text-foreground text-xs uppercase tracking-wider">
                      Koç İletişim Bilgileri
                    </p>
                    <div className="mt-2.5 space-y-1.5 text-muted-foreground text-xs">
                      <div className="flex items-center justify-between">
                        <span>Koç:</span>
                        <span className="font-medium text-foreground">
                          Alican Atalay
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Phone className="h-3 w-3" /> WhatsApp:
                        </span>
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-foreground underline-offset-4 hover:underline"
                        >
                          +90 544 964 28 24
                        </a>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Mail className="h-3 w-3" /> E-posta:
                        </span>
                        <a
                          href={mailUrl}
                          className="font-medium text-foreground underline-offset-4 hover:underline"
                        >
                          atalaythecoach@gmail.com
                        </a>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {isProgram && (
                <Button
                  asChild
                  size="lg"
                  className="h-12 w-full gap-2 rounded-xl font-bold"
                >
                  <Link
                    to={search.id ? "/program/$programId" : "/program"}
                    params={search.id ? { programId: search.id } : undefined}
                  >
                    <Dumbbell className="h-5 w-5" />
                    Programa Git
                  </Link>
                </Button>
              )}

              {isProduct && (
                <Button
                  asChild
                  size="lg"
                  className="h-12 w-full gap-2 rounded-xl font-bold"
                >
                  <Link to="/store">
                    <Package className="h-5 w-5" />
                    Mağazaya Dön
                  </Link>
                </Button>
              )}
            </div>

            {/* Home Link */}
            <div className="mt-6 border-border/60 border-t pt-4">
              <Link
                to="/"
                className="text-muted-foreground text-xs transition-colors hover:text-foreground"
              >
                Ana Sayfaya Dön
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
